import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { FlexBox, Box, Typography, Grid, MuiBox } from '@kis-technologies/kis-ui';
import scannerImage from 'assets/barcode_scan.png';
import { ERROR, USER_ERROR, NOT_CONNECTED, OPERATIONAL } from 'constants/hardware';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors } from 'services/configuration';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner } from 'services/hardware';
import { selectors as kioskSelectors, KioskHardware } from 'services/kiosk';
import { actions as ticketingActions } from 'services/ticketing';
import { useStyles } from '../styled';
import { LookupProps } from '../types';

const BarcodeScan = (props: LookupProps) => {
  const { onPrev, onNext, loading, error } = props;
  const { t, i18n } = useTranslation();

  const { scannerRead, resetScanner } = useActions(hardwareActions);
  const { resetMembership } = useActions(ticketingActions);
  const { status, loading: scannerLoading }: Scanner = useSelector(hardwareSelectors.selectScanner);
  const config = useSelector(configSelectors.selectConfiguration);
  const kiosk = useSelector(kioskSelectors.selectKiosk);

  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const membershipLookup = get(config, 'ticketing.membershipLookup');
  const messaging = get(membershipLookup, 'scanMembershipCard.messages');

  const classes = useStyles({ fontFamily })();

  // call scanner read: hits barcode scanner hardware
  React.useEffect(() => {
    if (!scannerLoading && !status.message && !error) {
      scannerRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // submit value read from scanner
  React.useEffect(() => {
    if (status.message && status.code === OPERATIONAL && !loading) {
      onNext({ memberId: status.message });
      resetScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.message]);

  const getBarcodeScanImgUrl = () => {
    if (kiosk) {
      const barcodeScanner = kiosk.hardware.find((device: KioskHardware) => device.type === 'barcodeScanner');
      if (barcodeScanner && barcodeScanner.imageUrl && barcodeScanner.imageUrl.primary) {
        return barcodeScanner.imageUrl.primary;
      }
    }
    return undefined;
  };

  const onPrevPageClick = () => {
    resetScanner();
    resetMembership();
    onPrev();
  };

  return (
    <>
      {/* when there is an error and hardware is not loading, show action overlay with hardware message set dynamically and action to retry scan action */}
      {[ERROR, USER_ERROR, NOT_CONNECTED].includes(status.code) && !scannerLoading && (
        <ActionOverlay
          title={
            <Box margin="0 0 5" width={710}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {t('commonText.somethingWentWrong')}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          onConfirm={scannerRead}
          cancelText={t('commonText.back')}
          onCancel={onPrevPageClick}
        />
      )}
      <Grid className={classes.membershipMainBlock}>
        <Grid className={classes.membershipContainer} container>
          <Grid item lg={12} xl={6} className={classes.scanTextContainer}>
            <FlexBox margin="0 0 8" direction="column">
              <Typography fontSize={100} fontWeight={600} sx={{ lineHeight: 1.1 }}>
                {get(messaging, `header.${i18n.language}`) || t('membershipScan.scanYourCard')}
              </Typography>
            </FlexBox>
            <MuiBox>
              <Typography className={classes.subHeading}>
                {get(messaging, `subheader.${i18n.language}`) || t('membershipScan.scanUsingBarcode')}
              </Typography>
            </MuiBox>
          </Grid>
          <Grid item lg={12} xl={6} className={classes.imageContainer}>
            <FlexBox fullHeight align="center" justify="center">
              {!getBarcodeScanImgUrl() && <img height={538} alt="url" src={scannerImage} />}
              {getBarcodeScanImgUrl() && <img width={500} height={538} alt="url" src={getBarcodeScanImgUrl()} />}
            </FlexBox>
          </Grid>
        </Grid>
      </Grid>
      {/* Footer */}
      <Grid container className={classes.footerContainer}>
        <Footer onPrevClick={onPrevPageClick} />
      </Grid>
    </>
  );
};

export default BarcodeScan;
